class ServingOption extends HTMLElement {
  constructor() {
    super();
    this.currentOption = null;
    this.isLoading = false;
    this.initialized = false;
    this.preventRecursion = false;
    this.options = null;
    this.storedHeaders = {
      optionsHeader: null,
      servingOptionTitle: null,
      subscribeHeader: null,
      subscribeHeaderSubscription: null,
      subscribeHeaderOtp: null,
    };
    this.freeShippingState = {
      showUnderPill: false,
      freeShippingText: "",
      isEnabled: false,
    };
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const optionsId = this.getAttribute("data-options-id");
    if (!optionsId) return;

    const optionsScript = document.getElementById(optionsId);
    if (!optionsScript) return;

    try {
      this.options = JSON.parse(optionsScript.textContent.trim());
      if (!this.currentOption) {
        this.currentOption = this.options[0];
      }
      this.storeHeaders();
      this.storeFreeShippingState();
      this.render();
      this.setupEventListeners();
      this.setupTabEventListeners();
    } catch (error) {
      console.error("Error parsing serving options:", error);
    }
  }

  storeHeaders() {
    const optionsHeader = document.querySelector(".options-header");
    const servingOptionTitle = document.querySelector(
      ".product-card__serving-option-title"
    );
    const subscribeHeader = document.querySelector(".subscribe-header");
    const subscribeHeaderSubscription = document.querySelector(
      ".subscribe-header--subscription"
    );
    const subscribeHeaderOtp = document.querySelector(".subscribe-header--otp");

    if (optionsHeader)
      this.storedHeaders.optionsHeader = optionsHeader.innerHTML;
    if (servingOptionTitle)
      this.storedHeaders.servingOptionTitle = servingOptionTitle.innerHTML;
    if (subscribeHeader)
      this.storedHeaders.subscribeHeader = subscribeHeader.innerHTML;
    if (subscribeHeaderSubscription)
      this.storedHeaders.subscribeHeaderSubscription =
        subscribeHeaderSubscription.innerHTML;
    if (subscribeHeaderOtp)
      this.storedHeaders.subscribeHeaderOtp = subscribeHeaderOtp.innerHTML;
  }

  storeFreeShippingState() {
    // Store the free shipping pill state
    const subscribePill = document.querySelector(
      '.tab-option[data-tab="subscribe"]'
    );
    const freeShippingElement = subscribePill?.querySelector(
      ".option-free-shipping"
    );

    if (freeShippingElement) {
      this.freeShippingState.isEnabled = true;
      this.freeShippingState.showUnderPill = true;
      this.freeShippingState.freeShippingText =
        freeShippingElement.textContent ||
        "Free shipping. Pause or cancel anytime.";
    } else {
      // Check if free shipping is in the delivery options
      const freeShippingOption = document.querySelector(
        ".option-free-shipping"
      );
      if (freeShippingOption) {
        this.freeShippingState.isEnabled = true;
        this.freeShippingState.showUnderPill = false;
        this.freeShippingState.freeShippingText =
          freeShippingOption.textContent ||
          "Free shipping. Pause or cancel anytime.";
      }
    }
  }

  setupTabEventListeners() {
    const purchaseBlock = this.parentElement.closest(
      ".product-card__purchase-block"
    );
    if (!purchaseBlock) return;

    const tabOptions = purchaseBlock.querySelectorAll(".tab-option");
    tabOptions.forEach((tab) => {
      tab.addEventListener("click", () =>
        this.toggleSubscribeHeaders(tab.dataset.tab)
      );
    });
  }

  toggleSubscribeHeaders(tabType) {
    const subscribeHeaderSubscription = document.querySelector(
      ".subscribe-header--subscription"
    );
    const subscribeHeaderOtp = document.querySelector(".subscribe-header--otp");

    if (!subscribeHeaderSubscription || !subscribeHeaderOtp) return;

    if (tabType === "subscribe") {
      subscribeHeaderSubscription.classList.remove("hidden");
      subscribeHeaderOtp.classList.add("hidden");
    } else if (tabType === "onetime") {
      subscribeHeaderSubscription.classList.add("hidden");
      subscribeHeaderOtp.classList.remove("hidden");
    }
  }

  render() {
    if (!this.options) return;

    this.innerHTML = `
          ${this.options
            .map(
              (option, index) => `
            <button 
              class="serving-option__button purchase-block__option ${
                this.currentOption === option ? "active" : ""
              }"
              role="tab"
              aria-selected="${this.currentOption === option}"
              aria-controls="serving-panel-${index}"
              data-index="${index}"
              ${
                this.currentOption === option ? 'tabindex="0"' : 'tabindex="-1"'
              }
            >
              <div class="serving-option__content">
                <span class="serving-option__title">${option.title}</span>
              </div>
            </button>
          `
            )
            .join("")}
    `;
  }

  setupEventListeners() {
    this.querySelectorAll(".serving-option__button").forEach((button) => {
      button.addEventListener("click", (e) => this.handleOptionClick(e));
      button.addEventListener("keydown", (e) => this.handleKeydown(e));
    });
  }

  async handleOptionClick(event) {
    const button = event.currentTarget;
    const index = parseInt(button.dataset.index);
    const selectedOption = this.options[index];

    if (
      this.isLoading ||
      this.currentOption === selectedOption ||
      this.preventRecursion
    )
      return;

    this.updateActiveButton(button);
    this.currentOption = selectedOption;

    // Only update loading state in the button
    button.classList.add("is-loading");

    await this.loadProductContent(selectedOption);

    button.classList.remove("is-loading");
  }

  handleKeydown(event) {
    const buttons = Array.from(
      this.querySelectorAll(".serving-option__button")
    );
    const index = buttons.indexOf(event.currentTarget);
    let newIndex;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        newIndex = (index + 1) % buttons.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        newIndex = (index - 1 + buttons.length) % buttons.length;
        break;
      default:
        return;
    }

    event.preventDefault();
    buttons[newIndex].click();
    buttons[newIndex].focus();
  }

  updateActiveButton(newActiveButton) {
    this.querySelectorAll(".serving-option__button").forEach((button) => {
      const isActive = button === newActiveButton;
      button.classList.toggle("active", isActive);
      button.setAttribute("aria-selected", isActive);
      button.setAttribute("tabindex", isActive ? "0" : "-1");
    });
  }

  async loadProductContent(option) {
    if (!option.handle) return;
    this.isLoading = true;

    const purchaseBlock = this.parentElement.closest(
      ".product-card__purchase-block"
    );
    const productContent = purchaseBlock.parentElement;
    if (!productContent || !purchaseBlock) return;

    try {
      const response = await fetch(
        `/products/${option.handle}?section_id=product-purchase-options`
      );
      if (!response.ok) throw new Error("Product fetch failed");

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const newContent = doc.querySelector("[data-api-content]");
      if (!newContent) return;

      const currentServingOptions = this.closest(
        ".product-card__serving-options"
      );
      const servingOptionsHtml = currentServingOptions.outerHTML;

      const newGiftContent = newContent.querySelector(".gift-list");
      const newGiftHtml = newGiftContent ? newGiftContent.outerHTML : "";

      const tempContent = document.createElement("div");
      tempContent.style.position = "absolute";
      tempContent.style.top = "0";
      tempContent.style.left = "0";
      tempContent.style.width = "100%";
      tempContent.style.opacity = "0";
      tempContent.innerHTML = newContent.innerHTML;

      const newPurchaseBlock = tempContent.querySelector(
        ".product-card__purchase-block"
      );
      if (newPurchaseBlock) {
        const newServingOptions = newPurchaseBlock.querySelector(
          ".product-card__serving-options"
        );
        if (newServingOptions) {
          newServingOptions.remove();
        }

        const newForm = newPurchaseBlock.querySelector("product-form form");
        const newVariantId = newForm?.querySelector('input[name="id"]')?.value;

        // Save the price element content
        const priceElements = newPurchaseBlock.querySelectorAll(
          "[data-js-product-price-extended]"
        );
        let priceElementHTML = "";
        if (priceElements.length > 0) {
          priceElementHTML = priceElements[0].innerHTML;
        }

        const purchaseBlockWrapper = purchaseBlock.querySelector(
          ".product-card__purchase-block-wrapper"
        );
        if (purchaseBlockWrapper) {
          const newPurchaseBlockWrapper = newPurchaseBlock.querySelector(
            ".product-card__purchase-block-wrapper"
          );
          if (newPurchaseBlockWrapper) {
            const existingWrapper = purchaseBlockWrapper;
            const newWrapperContent = newPurchaseBlockWrapper.innerHTML;

            existingWrapper.innerHTML = "";

            if (newGiftHtml) {
              const productGiftBlock =
                purchaseBlock.querySelector(".gift-list");
              if (productGiftBlock) {
                productGiftBlock.outerHTML = newGiftHtml;
              } else {
                purchaseBlock.insertAdjacentHTML("beforeend", newGiftHtml);
              }
            }
            existingWrapper.insertAdjacentHTML("beforeend", servingOptionsHtml);
            existingWrapper.insertAdjacentHTML("beforeend", newWrapperContent);
          } else {
            purchaseBlockWrapper.innerHTML =
              newGiftHtml + servingOptionsHtml + newPurchaseBlock.innerHTML;
          }
        } else {
          purchaseBlock.innerHTML =
            newGiftHtml + servingOptionsHtml + newPurchaseBlock.innerHTML;
        }

        // Update headers with stored values
        const optionsHeader = document.querySelector(".options-header");
        const servingOptionTitle = document.querySelector(
          ".product-card__serving-option-title"
        );
        const subscribeHeader = document.querySelector(".subscribe-header");
        const subscribeHeaderSubscription = document.querySelector(
          ".subscribe-header--subscription"
        );
        const subscribeHeaderOtp = document.querySelector(
          ".subscribe-header--otp"
        );

        if (optionsHeader && this.storedHeaders.optionsHeader) {
          optionsHeader.innerHTML = this.storedHeaders.optionsHeader;
        }
        if (servingOptionTitle && this.storedHeaders.servingOptionTitle) {
          servingOptionTitle.innerHTML = this.storedHeaders.servingOptionTitle;
        }
        if (subscribeHeader && this.storedHeaders.subscribeHeader) {
          subscribeHeader.innerHTML = this.storedHeaders.subscribeHeader;
        }
        if (
          subscribeHeaderSubscription &&
          this.storedHeaders.subscribeHeaderSubscription
        ) {
          subscribeHeaderSubscription.innerHTML =
            this.storedHeaders.subscribeHeaderSubscription;
        }
        if (subscribeHeaderOtp && this.storedHeaders.subscribeHeaderOtp) {
          subscribeHeaderOtp.innerHTML = this.storedHeaders.subscribeHeaderOtp;
        }

        // Apply saved price element content to all price elements
        if (priceElementHTML) {
          const currentPriceElements = purchaseBlock.querySelectorAll(
            "[data-js-product-price-extended]"
          );
          currentPriceElements.forEach((el) => {
            el.innerHTML = priceElementHTML;
          });
        }

        // Restore free shipping text state - only in the location it was originally found
        if (this.freeShippingState.isEnabled) {
          if (this.freeShippingState.showUnderPill) {
            // Only add free shipping text under subscription pill if it was there initially
            const subscribePill = purchaseBlock.querySelector(
              '.tab-option[data-tab="subscribe"]'
            );
            if (subscribePill) {
              // Remove any free shipping text from delivery options to avoid duplication
              const deliveryOptionShippingTexts =
                purchaseBlock.querySelectorAll(
                  ".delivery-option .option-free-shipping"
                );
              deliveryOptionShippingTexts.forEach((el) => el.remove());

              let freeShippingElement = subscribePill.querySelector(
                ".option-free-shipping"
              );
              if (!freeShippingElement) {
                subscribePill.insertAdjacentHTML(
                  "beforeend",
                  `<div class="option-free-shipping">${this.freeShippingState.freeShippingText}</div>`
                );

                // Ensure the tab selector has the proper padding if free shipping text is shown
                const tabSelector = subscribePill.closest(".tab-selector");
                if (tabSelector) {
                  tabSelector.classList.add("purchase-block__list-option");
                  if (!tabSelector.classList.contains("tab-selector")) {
                    tabSelector.classList.add("tab-selector");
                  }
                }
              } else {
                freeShippingElement.textContent =
                  this.freeShippingState.freeShippingText;
              }
            }
          } else {
            // Only add free shipping text in delivery options if it was there initially
            // First remove any free shipping text under subscription pill to avoid duplication
            const subscribePill = purchaseBlock.querySelector(
              '.tab-option[data-tab="subscribe"]'
            );
            if (subscribePill) {
              const pillShippingText = subscribePill.querySelector(
                ".option-free-shipping"
              );
              if (pillShippingText) pillShippingText.remove();
            }

            // Add to delivery options
            const deliveryOptions =
              purchaseBlock.querySelectorAll(".delivery-option");
            deliveryOptions.forEach((option) => {
              const optionDetails = option.querySelector(".option-details");
              if (optionDetails) {
                // Only add to subscription options, which typically have data-selling-plan attribute
                const sellingPlan = option.getAttribute("data-selling-plan");
                if (sellingPlan) {
                  let freeShippingElement = optionDetails.querySelector(
                    ".option-free-shipping"
                  );
                  if (!freeShippingElement) {
                    optionDetails.insertAdjacentHTML(
                      "beforeend",
                      `<div class="option-free-shipping">${this.freeShippingState.freeShippingText}</div>`
                    );
                  } else {
                    freeShippingElement.textContent =
                      this.freeShippingState.freeShippingText;
                  }
                }
              }
            });
          }
        }

        // Check active tab and apply header visibility
        const activeTab = purchaseBlock.querySelector(".tab-option.active");
        if (activeTab) {
          this.toggleSubscribeHeaders(activeTab.dataset.tab);
        }

        const servingOption = purchaseBlock.querySelector("serving-option");
        if (servingOption) {
          this.preventRecursion = true;

          const currentOptions = this.options;
          const currentSelectedOption = this.currentOption;

          servingOption.initialized = false;
          servingOption.options = currentOptions;
          servingOption.currentOption = currentSelectedOption;
          servingOption.storedHeaders = this.storedHeaders;
          servingOption.freeShippingState = this.freeShippingState;
          servingOption.connectedCallback();

          const buttons = servingOption.querySelectorAll(
            ".serving-option__button"
          );
          const activeButton =
            buttons[this.options.indexOf(this.currentOption)];
          if (activeButton) {
            servingOption.updateActiveButton(activeButton);
          }

          this.preventRecursion = false;
        }

        // Reinitialize subscription tabs
        const subscriptionTabs =
          purchaseBlock.querySelector("subscription-tabs");
        if (subscriptionTabs) {
          const oldTabs = subscriptionTabs.querySelectorAll("[data-tab]");
          oldTabs.forEach((tab) => {
            tab.replaceWith(tab.cloneNode(true));
          });

          if (!subscriptionTabs.initialized) {
            subscriptionTabs.connectedCallback();
          }

          // Reattach event listeners for tab switching
          const newServingOption =
            purchaseBlock.querySelector("serving-option");
          if (newServingOption) {
            newServingOption.setupTabEventListeners();
          }
        }

        // Reinitialize add to cart functionality
        const product_options =
          purchaseBlock.querySelectorAll(".delivery-option");
        const radio_inputs = purchaseBlock.querySelectorAll(
          '.delivery-option input[type="radio"]'
        );
        const atcButtons = purchaseBlock.querySelectorAll(".NEW-CUSTOM-ATC");

        // Reset event listeners for delivery options
        product_options.forEach((option) => {
          option.addEventListener("click", function (e) {
            option.querySelector(".option-radio").checked = true;
            option
              .querySelector(".option-radio")
              .dispatchEvent(new Event("change", { bubbles: true }));
            updateFormFromSelectedOption();
          });
        });

        // Reset event listeners for radio inputs
        radio_inputs.forEach((radio) => {
          radio.addEventListener("change", function (e) {
            updateFormFromSelectedOption(this);
          });
        });


        // Initialize first option
        const firstOption = purchaseBlock.querySelector(
          '.subscription-content:not(.hidden) .delivery-option input[type="radio"]'
        );
        if (firstOption) {
          firstOption.checked = true;
          firstOption.dispatchEvent(new Event("change", { bubbles: true }));
        }

        // Dispatch event to notify other components of the variant change
        document.dispatchEvent(
          new CustomEvent("variant:changed", {
            detail: { variantId: newVariantId },
          })
        );

        // Trigger form update
        updateFormFromSelectedOption();
      }
    } catch (error) {
      console.error("Error loading product content:", error);
      productContent.innerHTML = `
        <div class="serving-panel__error">
          <p>There was an error loading the product. Please try again.</p>
        </div>
      `;
    } finally {
      this.isLoading = false;
      this.preventRecursion = false;
    }
  }
}

if (!customElements.get("serving-option")) {
  customElements.define("serving-option", ServingOption);
}

// Add subscription-tabs component if not already defined
if (!customElements.get("subscription-tabs")) {
  class SubscriptionTabs extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.setupEventListeners();
    }

    setupEventListeners() {
      const tabs = this.querySelectorAll("[data-tab]");
      tabs.forEach((tab) => {
        // trigger custom event listener for tab clicks'
        tab.addEventListener("click", () => {
          this.switchTab(tab.dataset.tab)
        });
      });
    }

    switchTab(tabId) {
      // Update active tab
      const tabs = this.querySelectorAll("[data-tab]");
      tabs.forEach((tab) => {
        tab.classList.toggle("active", tab.dataset.tab === tabId);
      });

      // Toggle content visibility
      const subscribeContent = document.getElementById("subscribe-content");
      const onetimeContent = document.getElementById("onetime-content");

      if (subscribeContent && onetimeContent) {
        subscribeContent.classList.toggle("hidden", tabId !== "subscribe");
        onetimeContent.classList.toggle("hidden", tabId !== "onetime");
      }

      // Toggle header visibility
      const subscribeHeaderSubscription = document.querySelector(
        ".subscribe-header--subscription"
      );
      const subscribeHeaderOtp = document.querySelector(
        ".subscribe-header--otp"
      );

      if (subscribeHeaderSubscription && subscribeHeaderOtp) {
        subscribeHeaderSubscription.classList.toggle(
          "hidden",
          tabId !== "subscribe"
        );
        subscribeHeaderOtp.classList.toggle("hidden", tabId !== "onetime");
      }

      // Trigger form update with first available option
      const activeContent = document.querySelector(
        `.subscription-content:not(.hidden)`
      );
      if (activeContent) {
        const firstOption = activeContent.querySelector(
          '.delivery-option input[type="radio"]'
        );
        if (firstOption) {
          firstOption.checked = true;
          firstOption.dispatchEvent(new Event("change", { bubbles: true }));
          if (typeof updateFormFromSelectedOption === "function") {
            updateFormFromSelectedOption(firstOption);
          }
        }
      }
    }
  }

  customElements.define("subscription-tabs", SubscriptionTabs);
}

// Helper function to update form from selected option
function updateFormFromSelectedOption(product_option_selected = null) {
  // If no option provided, find the checked one
  if (!product_option_selected) {
    product_option_selected = document.querySelector(
      ".subscription-content:not(.hidden) .delivery-option input:checked"
    );
    if (!product_option_selected) return; // Exit if no selected option found
  }

  let product_form = document.querySelector(".product-form");
  let productOptionSelectedData =
    product_option_selected.closest(".delivery-option");

  // Get the product and variant IDs
  const productId = productOptionSelectedData.getAttribute("data-product-id");
  const variantId =
    productOptionSelectedData.getAttribute("data-variant-id") || productId;
  const sellingPlanId =
    productOptionSelectedData.getAttribute("data-selling-plan");
  const sellingPlanName = productOptionSelectedData.getAttribute(
    "data-selling-plan-name"
  );

  // Set quantity based on a product type
  let quantity =
    parseInt(productOptionSelectedData.dataset.packSize || "1", 10) ||
    parseInt(productOptionSelectedData.dataset.quantity || "1", 10);
  if (productOptionSelectedData.dataset.productOtp === "true") {
    quantity = parseInt(productOptionSelectedData.dataset.quantity || "1", 10);
    product_form.querySelector('input[name="quantity"]').value = quantity;
  }

  // Update the price
  let priceValue = parseFloat(
    productOptionSelectedData.getAttribute("data-price") || "0"
  );
  let compareAtPrice = parseFloat(
    productOptionSelectedData.getAttribute("data-full-price") || "0"
  );
  // Calculate total prices based on quantity
  const totalPrice = priceValue * quantity;
  const totalComparePrice = compareAtPrice * quantity;

  // Subscription
  DisplayPrice(
    priceValue / 100,
    compareAtPrice !== null && compareAtPrice !== priceValue
      ? totalComparePrice / 100
      : undefined,
    quantity,
    productId
  );

  // Update form values
  product_form.querySelector('input[name="id"]').value = variantId;
  product_form.querySelector('input[name="selling_plan"]').value =
    sellingPlanId;
  product_form.querySelector('input[name="option-0"]').value = sellingPlanName;

  // Dispatch event to update product display
  const event = new CustomEvent("product:variant:change", {
    detail: {
      productId: productId,
      variantId: variantId,
      sellingPlanId: sellingPlanId,
      sellingPlanName: sellingPlanName,
    },
  });
  document.dispatchEvent(event);
}
